import React, { useCallback, useEffect, useState } from 'react';
import { ReactSVG } from 'react-svg';
import useIsMobile from 'Hooks/Common/useIsMobile';
import classNames from 'classnames';
import CloseIcon from 'Assets/images/icons/close-icon.svg';
import DayPicker, {
  RangeModifier,
  DateUtils,
  DayModifiers,
  Modifier,
} from 'react-day-picker';
import { DayElement, NavbarElement } from '..';
import UIButton from 'Components/UIComponents/UIButton';

const weekdaysShort = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

export interface CalendarProps {
  onDateSelect?: (date: Date) => void;
  onDateRangeSelect?: (dateRange: RangeModifier) => void;
  onCancel?: () => void;
  onHide?: () => void;
  value?: RangeModifier | Date;
  disabledDays?: Modifier | Modifier[];
  fromMonth?: Date;
  toMonth?: Date;
  submitButtonTitle?: string;
  wrapperClassName?: string;
}

const isDateRange = (date: RangeModifier | Date): date is RangeModifier => {
  return (date as RangeModifier).from !== undefined;
};

const isDate = (date: RangeModifier | Date): date is Date => {
  return (date as Date).getTime !== undefined;
};

const Calendar = ({
  onDateSelect,
  onDateRangeSelect,
  onCancel,
  onHide,
  value,
  disabledDays,
  fromMonth,
  toMonth,
  submitButtonTitle = 'Select',
  wrapperClassName,
}: CalendarProps) => {
  const isMobile = useIsMobile();

  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedDateRange, setSelectedDateRange] = useState<RangeModifier>();

  useEffect(() => {
    if (value && isDateRange(value)) {
      return setSelectedDateRange(value);
    }

    if (value && isDate(value)) {
      return setSelectedDate(value);
    }
  }, [value]);

  const handleSelectButtonClick = () => {
    if (onDateRangeSelect && selectedDateRange) onDateRangeSelect(selectedDateRange);

    if (onDateSelect && selectedDate) onDateSelect(selectedDate);

    if (onHide) onHide();
  };

  const handleDayClick = useCallback(
    (day: Date, { disabled }: DayModifiers) => {
      if (disabled) {
        return;
      }

      if (!onDateRangeSelect) {
        return setSelectedDate(day);
      }

      if (!selectedDateRange) {
        return setSelectedDateRange({ from: day, to: day });
      }
      const range = DateUtils.addDayToRange(day, selectedDateRange);

      if (range.from && range.to) {
        return setSelectedDateRange(range);
      }
    },
    [onDateRangeSelect, selectedDateRange],
  );

  const handleClickCancel = () => {
    if (onHide) onHide();
    if (onCancel) onCancel();
  };

  const rangeDays = selectedDateRange
    ? [selectedDateRange.from, selectedDateRange.to]
    : [selectedDate];

  const modifiers = {
    highlighted: [...rangeDays],
    disabled: disabledDays,
    inRange: selectedDateRange,
    saturday: { daysOfWeek: [6] },
    sunday: { daysOfWeek: [0] },
    ...selectedDateRange,
  };

  return (
    <div
      className={classNames('DayPicker__wrapper', wrapperClassName, { mobile: isMobile })}
    >
      {onHide && (
        <ReactSVG src={CloseIcon} onClick={onHide} className="DayPicker__close-button" />
      )}
      <DayPicker
        modifiers={modifiers}
        fromMonth={fromMonth}
        toMonth={toMonth}
        weekdaysShort={weekdaysShort}
        renderDay={(day, modifiers) => (
          <DayElement day={day} modifiers={modifiers} selectedRange={selectedDateRange} />
        )}
        navbarElement={NavbarElement}
        onDayClick={handleDayClick}
      />
      <div className="DayPicker__footer">
        <div
          className="DayPicker__button DayPicker__button--cancel"
          onClick={handleClickCancel}
        >
          Cancel
        </div>
        <div className="DayPicker__button DayPicker__button--select">
          <UIButton
            title={submitButtonTitle}
            handleClick={handleSelectButtonClick}
            priority="primary"
            disabled={!selectedDate && !selectedDateRange}
          />
        </div>
      </div>
    </div>
  );
};

export default Calendar;
